'use client';

import React, { useContext, useState, useRef, useEffect } from 'react';
import { AuthContext } from '../../context/AuthContext';
import { supabase } from '../../supabaseClient';
import Layout from '../Layout';
import { parsePqidatFile, fetchOrganizationMeasurements, findNewRecords, uploadNewRecords, analyzeFileData } from '../../utils/processMeasurements';
import { Link } from 'react-router-dom'; // Import Link for navigation

export default function Upload() {
  const { user } = useContext(AuthContext);
  const [selectedFile, setSelectedFile] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [newRecords, setNewRecords] = useState(null);
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [displayedCount, setDisplayedCount] = useState(5);
  const fileInputRef = useRef(null);
  const [uploadStatus, setUploadStatus] = useState('idle');
  const [measurementsLeft, setMeasurementsLeft] = useState(null);
  const [isQuotaLoading, setIsQuotaLoading] = useState(true);
  const [fileAnalysis, setFileAnalysis] = useState(null);


  useEffect(() => {
    if (error === 'No new records found in the uploaded file.') {
      console.log('Error state updated:', error);
    }
  }, [error]);

  // Fetch remaining measurements on page load
  useEffect(() => {
  const fetchQuota = async () => {
    if (!user) return;
    try {
      const { data: orgMember, error: orgError } = await supabase
        .from('organization_members')
        .select('organization_id')
        .eq('user_id', user.id)
        .single();
      if (orgError || !orgMember) throw new Error('User not in an organization');

      const organizationId = orgMember.organization_id;

      // Use the Supabase client's invoke method instead of raw fetch
      // This automatically includes the user's JWT in the Authorization header
      // if the user is signed in (which they are, since 'user' exists)
      const { data, error } = await supabase.functions.invoke('get-team-usage', {
        method: 'POST',
        body: { organizationId },
      });

      if (error) {
        throw new Error(`Failed to fetch team usage: ${error.message}`);
      }

      setMeasurementsLeft(data.measurementsLeft);
    } catch (err) {
      console.error('Error fetching measurement quota:', err.message);
      setError('Failed to fetch measurement quota. Please try again later.');
    } finally {
      setIsQuotaLoading(false);
    }
  };

  fetchQuota();
}, [user]);

  // Handle file selection and comparison
  const handleFileChange = async (event) => {
  const file = event.target.files[0];
  if (file) {
    if (file.name.toLowerCase().endsWith('.pqidat')) {
      try {
        const pqidatRecords = await parsePqidatFile(file);
        
        // Analyze the file data first
        const analysis = analyzeFileData(pqidatRecords);
        
        if (!analysis.hasValidData) {
          setError('The uploaded file contains no valid measurement data with proper date/time.');
          setSelectedFile(null);
          if (fileInputRef.current) {
            fileInputRef.current.value = null;
          }
          return;
        }

        const existingMeasurements = await fetchOrganizationMeasurements(user.id);
        const results = findNewRecords(pqidatRecords, existingMeasurements);
        
        console.log('Analysis Results:', analysis);
        console.log('Duplicate Detection Results:', results);

        if (results.newRecords.length === 0) {
          if (results.duplicatesFound > 0) {
            setError('All measurements in this file have already been uploaded. Please try uploading a different file with new measurements.');
          } else {
            setError('No valid measurements found in the uploaded file.');
          }
          setSelectedFile(null);
          if (fileInputRef.current) {
            fileInputRef.current.value = null;
          }
          return;
        }

        // Handle quota limits
        let newRecordsFound = results.newRecords;
        if (measurementsLeft !== null && newRecordsFound.length > measurementsLeft) {
          setError(
            `The uploaded file contains ${newRecordsFound.length} measurements, but your organization has only ${measurementsLeft} measurements left in this billing cycle. Only the first ${measurementsLeft} records will be uploaded.`
          );
          newRecordsFound = newRecordsFound.slice(0, measurementsLeft);
        }

        // Set success state with GPS warning if needed
        setSelectedFile(file);
        setNewRecords(newRecordsFound);
        setSelectedRecords(newRecordsFound);
        setSuccess(false);
        
        // Store analysis results for UI display
        setFileAnalysis(analysis);
        
      } catch (err) {
        setError(err.message);
        setSelectedFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = null;
        }
      }
    } else {
      setError('Please upload a valid .pqidat file.');
      if (fileInputRef.current) {
        fileInputRef.current.value = null;
      }
    }
  }
};

const GPSWarningBanner = ({ analysis }) => {
  if (!analysis || analysis.recordsWithoutGPS === 0) return null;
  
  const isAllWithoutGPS = analysis.recordsWithGPS === 0;
  const isPartialWithoutGPS = analysis.recordsWithGPS > 0 && analysis.recordsWithoutGPS > 0;
  
  return (
    <div className={`mt-4 mb-4 p-4 rounded-lg border-l-4 ${
      isAllWithoutGPS ? 'bg-yellow-50 border-yellow-400' : 'bg-blue-50 border-blue-400'
    }`}>
      <div className="flex">
        <div className="flex-shrink-0">
          <svg 
            className={`h-5 w-5 ${isAllWithoutGPS ? 'text-yellow-400' : 'text-blue-400'}`} 
            xmlns="http://www.w3.org/2000/svg" 
            viewBox="0 0 20 20" 
            fill="currentColor"
          >
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
        </div>
        <div className="ml-3">
          {isAllWithoutGPS ? (
            <div>
              <p className="text-sm font-medium text-yellow-800">
                GPS Data Missing
              </p>
              <p className="text-sm text-yellow-700">
                This file contains no GPS coordinates. Measurements will be uploaded but won't have location data. 
                Duplicate detection will be based on timestamp only.
              </p>
            </div>
          ) : (
            <div>
              <p className="text-sm font-medium text-blue-800">
                Partial GPS Data
              </p>
              <p className="text-sm text-blue-700">
                {analysis.recordsWithGPS} measurements have GPS data, {analysis.recordsWithoutGPS} do not. 
                Records without GPS will use timestamp-only duplicate detection.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
  // Handle file upload to Supabase
  const handleUpload = async () => {
    if (selectedRecords.length === 0) {
      setError('Please select at least one record to upload.');
      return;
    }

    try {
      setUploadStatus('uploading');
      await new Promise(resolve => setTimeout(resolve, 1500));
      setUploadStatus('mapping');
      await new Promise(resolve => setTimeout(resolve, 1500));
      await uploadNewRecords(selectedRecords, user.id);
      setUploadStatus('success');
      setTimeout(() => {
        setUploadStatus('idle');
        setSelectedFile(null);
        setNewRecords(null);
        setSelectedRecords([]);
        setDisplayedCount(5);
        if (fileInputRef.current) {
          fileInputRef.current.value = null;
        }
        // Refresh the measurements quota after upload
        const fetchQuota = async () => {
          try {
            const { data: orgMember } = await supabase
              .from('organization_members')
              .select('organization_id')
              .eq('user_id', user.id)
              .single();
            const response = await fetch('https://zccgzqkkpmqpawelftbw.supabase.co/functions/v1/get-team-usage', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ organizationId: orgMember.organization_id }),
            });
            const data = await response.json();
            setMeasurementsLeft(data.measurementsLeft);
          } catch (err) {
            console.error('Error refreshing measurement quota:', err.message);
          }
        };
        fetchQuota();
      }, 1500);
    } catch (err) {
      setError(err.message);
      setUploadStatus('idle');
    }
  };

  // Trigger file input click when the upload button is clicked
  const handleUploadButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Handle record selection
  const toggleRecordSelection = (record) => {
    setSelectedRecords((prev) =>
      prev.includes(record)
        ? prev.filter((r) => r !== record)
        : [...prev, record]
    );
  };

  // Handle select all
  const toggleSelectAll = () => {
    if (selectedRecords.length === newRecords.length) {
      setSelectedRecords([]);
    } else {
      // Limit the selection to the remaining quota
      if (measurementsLeft !== null && newRecords.length > measurementsLeft) {
        setSelectedRecords(newRecords.slice(0, measurementsLeft));
      } else {
        setSelectedRecords([...newRecords]);
      }
    }
  };

  // Handle "Show More" / "Show Less"
  const handleShowMore = () => {
    if (displayedCount >= newRecords.length) {
      setDisplayedCount(5);
    } else {
      setDisplayedCount((prev) => Math.min(prev + 10, newRecords.length));
    }
  };

  // Display records up to the current displayedCount
  const displayedRecords = newRecords?.slice(0, displayedCount);

  return (
    <Layout>
      <div className="p-4">
        {/* Header with Quota Indicator */}
        <div className="flex flex-col space-y-4 mb-4">
          <div className="flex justify-between items-center">
            <h1 className="text-3xl font-bold text-gray-900">Upload Measurements</h1>
            <div className="flex items-center space-x-2">
              {isQuotaLoading ? (
                <span className="text-sm text-gray-500">Loading quota...</span>
              ) : measurementsLeft !== null ? (
                <span
                  className={`inline-flex items-center px-4 py-2 rounded-full text-xs font-semibold border-2 ${
                    measurementsLeft > 0
                      ? 'bg-green-200 text-green-900 border-green-400'
                      : 'bg-red-200 text-red-900 border-red-400'
                  }`}
                >
                  {measurementsLeft} Measurements Left in This Billing Cycle
                </span>
              ) : (
                <span className="text-sm text-red-500">Quota unavailable</span>
              )}
            </div>
          </div>
          {/* Warning Message in Header */}
          {error && error.includes('The uploaded file contains') && (
            <div className="rounded-lg bg-gradient-to-r from-red-900/80 to-red-800/80 p-4 shadow-md transition-all duration-300 hover:shadow-lg">
              <div className="flex items-center gap-x-2">
                <svg
                  className="w-5 h-5 text-red-400 animate-pulse"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
                <p className="text-sm font-semibold text-red-200">{error}</p>
              </div>
            </div>
          )}
        </div>
        <p className="text-gray-600 mb-8">
          Upload a .pqidat file from your TransTech gauge to process and analyze your measurements.
        </p>

        {error === 'No new records found in the uploaded file.' && (
  <div className="mt-4 mb-6 p-4 bg-yellow-50 border-l-4 border-yellow-400 rounded-lg">
    <div className="flex">
      <div className="flex-shrink-0">
        <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
        </svg>
      </div>
      <div className="ml-3">
        <p className="text-sm font-medium text-yellow-800">
          All measurements in this file have already been uploaded. Please try uploading a different file with new measurements.
        </p>
      </div>
    </div>
  </div>
)}

        {/* File Input (Always Rendered, Hidden) */}
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          accept=".pqidat"
          className="hidden"
          disabled={isQuotaLoading || measurementsLeft === 0}
        />

        {/* Empty State or File Selection */}
        <div className="bg-white rounded-lg p-8 border-2 border-gray-200">
          {isQuotaLoading ? (
            // Loading State
            <div className="flex flex-col items-center space-y-6">
              <div className="p-4 bg-gray-100 rounded-full">
                <svg
                  className="animate-spin h-12 w-12 text-gray-400"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              </div>
              <div className="text-center">
                <h3 className="text-lg font-semibold text-gray-900">Loading...</h3>
                <p className="mt-2 text-sm text-gray-600">Fetching your measurement quota...</p>
              </div>
            </div>
          ) : measurementsLeft === 0 ? (
            // Empty State for Zero Measurements Left
            <div className="flex flex-col items-center space-y-6">
              <div className="p-4 bg-red-100 rounded-full">
                <svg
                  className="w-12 h-12 text-red-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              <div className="text-center">
                <h3 className="text-lg font-semibold text-gray-900">No Measurements Left</h3>
                <p className="mt-2 text-sm text-gray-600">
                  You've reached your measurement limit for this billing cycle. Upgrade your subscription to upload more.
                </p>
              </div>
              <Link
                to="/settings/Billing"
                className="inline-flex items-center px-6 py-3 bg-yellow-600 text-white text-sm font-semibold rounded-lg shadow-md hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2 transition-all duration-200"
              >
                <svg
                  className="w-5 h-5 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                  />
                </svg>
                Upgrade Subscription
              </Link>
            </div>
          ) : newRecords ? (
            // New Records Found - Confirmation State
            <div className="flex flex-col space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                  New Measurements Found ({newRecords.length})
                </h3>
                <button
                  onClick={() => {
                    setSelectedFile(null);
                    setNewRecords(null);
                    setSelectedRecords([]);
                    setDisplayedCount(5);
                    if (fileInputRef.current) {
                      fileInputRef.current.value = null;
                    }
                    setError(null);
                    setSuccess(false);
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <GPSWarningBanner analysis={fileAnalysis} />

              {/* Error Message */}
              {error && !error.includes('The uploaded file contains') && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-600">{error} <a className="underline" href='/settings/Billing'>Manage Subscription</a></p>
                </div>
              )}

              {/* Refined Table of New Records */}
              <div className="overflow-x-auto rounded-lg border-2">
  <table className="min-w-full divide-y divide-gray-200">
    <thead className="bg-gray-50">
      <tr>
        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
          <input
            type="checkbox"
            checked={selectedRecords.length === newRecords.length}
            onChange={toggleSelectAll}
            className="rounded border-gray-300 text-yellow-600 focus:ring-yellow-600 cursor-pointer"
          />
        </th>
        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
          Proj Name
        </th>
        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
          Location
        </th>
        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
          Density
        </th>
        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
          Compaction
        </th>
        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
          Surf Temp
        </th>
        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
          GPS Status
        </th>
        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
          Date/Time
        </th>
      </tr>
    </thead>
    <tbody className="bg-white divide-y divide-gray-100">
      {displayedRecords.map((record, index) => {
        const hasGPS = record.GPS && record.GPS.trim();
        
        return (
          <tr
            key={index}
            className="hover:bg-gray-50 transition-colors duration-150"
          >
            <td className="px-4 py-4 whitespace-nowrap">
              <input
                type="checkbox"
                checked={selectedRecords.includes(record)}
                onChange={() => toggleRecordSelection(record)}
                className="rounded border-gray-300 text-yellow-600 focus:ring-yellow-600 cursor-pointer"
              />
            </td>
            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-800 font-medium">
              {record.ProjName || 'N/A'}
            </td>
            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
              {record.Location1 || 'N/A'}
            </td>
            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
              {record.Density || 'N/A'}
            </td>
            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
              {record.Compaction || 'N/A'}
            </td>
            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
              {record.SurfTemp || 'N/A'}
            </td>
            <td className="px-4 py-4 whitespace-nowrap">
              {hasGPS ? (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  GPS
                </span>
              ) : (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                  <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  No GPS
                </span>
              )}
            </td>
            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
              {record.DateTime || 'N/A'}
            </td>
          </tr>
        );
      })}
    </tbody>
  </table>
</div>

              {/* Show More / Show Less Button */}
              {newRecords.length > 5 && (
                <div className="flex justify-center mt-4">
                  <button
                    onClick={handleShowMore}
                    className="inline-flex items-center px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-300 transition-all duration-200"
                  >
                    {displayedCount >= newRecords.length ? 'Show Less' : 'Show More'}
                    <svg
                      className={`w-4 h-4 ml-2 transform ${displayedCount >= newRecords.length ? 'rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex justify-end space-x-4">
                <button
                  onClick={() => {
                    setSelectedFile(null);
                    setNewRecords(null);
                    setSelectedRecords([]);
                    setDisplayedCount(5);
                    if (fileInputRef.current) {
                      fileInputRef.current.value = null;
                    }
                    setError(null);
                    setSuccess(false);
                  }}
                  className="inline-flex items-center px-6 py-3 bg-gray-200 text-gray-700 text-sm font-semibold rounded-lg shadow-md border-2 hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-all duration-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpload}
                  className={`inline-flex items-center px-6 py-3 text-white text-sm font-semibold rounded-lg shadow-md focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all duration-200 ${
                    uploadStatus !== 'idle' ? 'bg-yellow-600' : uploadStatus === 'success' ? 'bg-green-600' : 'bg-yellow-600 hover:bg-yellow-700 focus:ring-yellow-500'
                  }`}
                  disabled={uploadStatus !== 'idle'}
                >
                  {uploadStatus === 'uploading' ? (
                    <>
                      <svg className="w-5 h-5 mr-2 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      Uploading...
                    </>
                  ) : uploadStatus === 'mapping' ? (
                    <>
                      <svg className="w-5 h-5 mr-2 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      Mapping to Projects...
                    </>
                  ) : uploadStatus === 'success' ? (
                    <>
                      <svg
                        className="w-5 h-5 mr-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                      Measurements Uploaded!
                    </>
                  ) : (
                    <>Confirm Upload</>
                  )}
                </button>
              </div>
            </div>
          ) : (
            // Empty State
            <div className="flex flex-col items-center space-y-6">
              {/* Icon */}
              <div className="p-4 bg-gray-100 rounded-full">
                <svg
                  className="w-12 h-12 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 4v16m8-8H4"
                  />
                </svg>
              </div>

              {/* Message */}
              <div className="text-center">
                <h3 className="text-lg font-semibold text-gray-900">
                  No File Selected
                </h3>
                <p className="mt-2 text-sm text-gray-600">
                  Upload a .pqidat file from your TransTech gauge to get started.
                </p>
              </div>

              {/* Upload Button */}
              <button
                onClick={handleUploadButtonClick}
                className={`inline-flex items-center px-6 py-3 text-white text-sm font-semibold rounded-lg shadow-md focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all duration-200 ${
                  isQuotaLoading
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-yellow-600 hover:bg-yellow-700 focus:ring-yellow-500'
                }`}
                disabled={isQuotaLoading}
              >
                {isQuotaLoading ? (
                  <>
                    <svg
                      className="animate-spin h-5 w-5 mr-2 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Loading Quota...
                  </>
                ) : (
                  <>
                    <svg
                      className="w-5 h-5 mr-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                      />
                    </svg>
                    Upload .pqidat File
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}